import { Injectable, signal } from '@angular/core';

import { ITimesheet } from '@core/interfaces';

export interface EditingTimesheet {
  original: ITimesheet;
  current: ITimesheet;
}

@Injectable({ providedIn: 'root' })
export class TimesheetFormService {
  private editingTimesheets = signal<Map<number, EditingTimesheet>>(new Map());

  /**
   * Check if a specific timesheet is being edited.
   */
  isEditing(timesheetId: number): boolean {
    return this.editingTimesheets().has(timesheetId);
  }

  /**
   * Check if any timesheet is being edited.
   */
  isAnyEditing(): boolean {
    return this.editingTimesheets().size > 0;
  }

  /**
   * Get the current editing state for a timesheet.
   */
  getEditingTimesheet(timesheetId: number): ITimesheet | undefined {
    return this.editingTimesheets().get(timesheetId)?.current;
  }

  /**
   * Get the key to use for a timesheet (id if available, otherwise source_id).
   */
  private getTimesheetKey(timesheet: ITimesheet): number {
    return timesheet.id || timesheet.source_id;
  }

  /**
   * Start editing a timesheet. Creates a deep copy for editing.
   */
  startEditing(timesheet: ITimesheet): void {
    const key = this.getTimesheetKey(timesheet);
    if (!key) return;

    this.editingTimesheets.update((map) => {
      const newMap = new Map(map);
      newMap.set(key, {
        original: structuredClone(timesheet),
        current: structuredClone(timesheet),
      });
      return newMap;
    });
  }

  /**
   * Update an entry's submitted hours while editing.
   */
  updateEntry(timesheetId: number, date: string, hours: number): void {
    this.editingTimesheets.update((map) => {
      const editing = map.get(timesheetId);
      if (!editing) return map;

      const entry = editing.current.entries.find((e) => e.date === date);
      if (entry) {
        entry.submitted_hours = hours;
        entry.is_modified = true;
      }

      const newMap = new Map(map);
      newMap.set(timesheetId, { ...editing });
      return newMap;
    });
  }

  /**
   * Cancel editing and return the original timesheet.
   */
  cancelEditing(timesheetId: number): ITimesheet | undefined {
    const editing = this.editingTimesheets().get(timesheetId);
    if (!editing) return undefined;

    this.editingTimesheets.update((map) => {
      const newMap = new Map(map);
      newMap.delete(timesheetId);
      return newMap;
    });

    return editing.original;
  }

  /**
   * Commit editing and return the modified timesheet.
   */
  commitEditing(timesheetId: number): ITimesheet | undefined {
    const editing = this.editingTimesheets().get(timesheetId);
    if (!editing) return undefined;

    this.editingTimesheets.update((map) => {
      const newMap = new Map(map);
      newMap.delete(timesheetId);
      return newMap;
    });

    return editing.current;
  }

  /**
   * Check if a timesheet has unsaved changes.
   */
  isDirty(timesheetId: number): boolean {
    const editing = this.editingTimesheets().get(timesheetId);
    if (!editing) return false;

    return JSON.stringify(editing.original.entries) !== JSON.stringify(editing.current.entries);
  }

  /**
   * Clear all editing state.
   */
  clearAll(): void {
    this.editingTimesheets.set(new Map());
  }
}
